import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/system';
import RenderSleeperSupplements from './RenderSleeperSupplements';
import { getCheapestSupplements } from './SleeperSupplementUtils';
import styles from './styles';

const StyledPassengerAccordionSummary = styled(AccordionSummary)(() => ({
	...styles.accordionSummary,
	fontSize: '0.8rem',
}));

const SleeperSupplementTravelSegment = ({
	legid,
	travelSegments,
	searchedSleeperSupplements,
	onCheckSleeperSupplement,
	selectedSleeperSupplements,
	passengerNumber,
}) => {
	const [expandedPassenger, setExpandedPassenger] = useState(null);

	const handlePassengerAccordionChange =
		(passengerId) => (event, isExpanded) => {
			setExpandedPassenger(isExpanded ? passengerId : null);
		};

	return (
		<>
			{travelSegments[legid].map((travelSegment) => {
				// Group supplements by passenger
				const supplementsByPassenger = searchedSleeperSupplements
					.filter((supplement) =>
						supplement.linkedTravelSegments.includes(
							travelSegment.TravelSegmentID,
						),
					)
					.reduce((acc, supplement) => {
						const passengerId = supplement.linkedPassengers[0];
						if (!acc[passengerId]) {
							acc[passengerId] = [];
						}
						acc[passengerId].push(supplement);
						return acc;
					}, {});

				// No render if no supplements for this travel segment
				if (Object.keys(supplementsByPassenger).length === 0) {
					return null;
				}

				const hasMultiplePassengers =
					Object.keys(supplementsByPassenger).length > 1;

				return (
					<Grid item xs={12} key={travelSegment.TravelSegmentID}>
						<Grid container spacing={1}>
							{Object.entries(supplementsByPassenger).map(
								([passengerId, supplements], index) => {
									const cheapestSupplements = getCheapestSupplements(
										supplements,
										passengerNumber,
									);
									// Single passenger (no accordion)
									if (!hasMultiplePassengers) {
										return (
											<Grid item xs={12} key={passengerId}>
												<RenderSleeperSupplements
													key={`RenderSleeperSupplements${passengerId}_${travelSegment.TravelSegmentID}`}
													sleeperSupplements={cheapestSupplements}
													onCheckSleeperSupplement={onCheckSleeperSupplement}
													selectedSleeperSupplements={
														selectedSleeperSupplements
													}
												/>
											</Grid>
										);
									}

									// Multiple passengers
									return (
										<Grid item xs={12} key={passengerId}>
											<Accordion
												expanded={expandedPassenger === passengerId}
												onChange={handlePassengerAccordionChange(passengerId)}
											>
												<StyledPassengerAccordionSummary
													expandIcon={<ExpandMoreIcon />}
												>
													{`Passenger ${index + 1}`}
												</StyledPassengerAccordionSummary>
												<AccordionDetails>
													<RenderSleeperSupplements
														key={`RenderSleeperSupplements${passengerId}_${travelSegment.TravelSegmentID}`}
														sleeperSupplements={cheapestSupplements}
														onCheckSleeperSupplement={onCheckSleeperSupplement}
														selectedSleeperSupplements={
															selectedSleeperSupplements
														}
													/>
												</AccordionDetails>
											</Accordion>
										</Grid>
									);
								},
							)}
						</Grid>
					</Grid>
				);
			})}
		</>
	);
};

SleeperSupplementTravelSegment.propTypes = {
	onCheckSleeperSupplement: PropTypes.func.isRequired,
	travelSegments: PropTypes.array.isRequired,
	searchedSleeperSupplements: PropTypes.array.isRequired,
	selectedSleeperSupplements: PropTypes.array.isRequired,
	legid: PropTypes.number.isRequired,
	passengerNumber: PropTypes.number.isRequired,
};

export { SleeperSupplementTravelSegment as SleeperSupplementTravelSegmentAlias };

export default SleeperSupplementTravelSegment;
